import { forwardRef, useState } from "react";
import { Accordion } from "../core/Accordion/Accordion";
import {
  OrgAccordionContent,
  OrgAccordionOption,
  OrgAccordionContainer,
} from "./OrgAccordion.styled";
import { Checkbox } from "../core/Checkbox/Checkbox";
import ToggleSwitch from "../core/ToggleSwitch/ToggleSwitch";
import {
  OrgAreaItem,
  OrgCCItem,
  OrgPracticeItem,
} from "../../types/api/org.type";
import { ALL_LABEL } from "../../constants/Dictionary";
import { Chip, ChipSizeEnum } from "../core/Chip/Chip";
import { useAppSelector } from "../../hooks/store.hook";

interface OrgAccordionProps {
  onChange: (value: string[]) => void;
  value?: string[];
}

export const OrgAccordion = forwardRef<HTMLDivElement, OrgAccordionProps>(
  ({ value = [], onChange }, ref) => {
    const { orgSettings: org } = useAppSelector((state) => state.config);

    const [selectedArea, setSelectedArea] = useState<OrgAreaItem>(
      org.areaList.filtered[0],
    );

    const practiceList = selectedArea.practiceList;

    const allCcInArea = practiceList.flatMap((practice) => practice.ccList);

    const areAllSelectedInArea = allCcInArea.every((cc) =>
      value.some((selectedCc) => selectedCc === cc.id),
    );

    const toggleAllCcsInArea = (checked: boolean) => {
      if (checked) {
        const toAdd = allCcInArea.filter(
          (cc) => !value.some((selectedCc) => selectedCc === cc.id),
        );
        onChange([...value, ...toAdd.map((item) => item.id)]);
      } else {
        onChange(
          value.filter(
            (selectedCc) => !allCcInArea.some((cc) => cc.id === selectedCc),
          ),
        );
      }
    };

    const toggleAllCcsInPractice = (
      practice: OrgPracticeItem,
      checked: boolean,
    ) => {
      const ccList = practice.ccList;
      if (checked) {
        const toAdd = ccList.filter(
          (cc) => !value.some((selectedCc) => selectedCc === cc.id),
        );
        onChange([...value, ...toAdd.map((item) => item.id)]);
      } else {
        onChange(
          value.filter(
            (selectedCc) => !ccList.some((cc) => cc.id === selectedCc),
          ),
        );
      }
    };

    const toggleCc = (cc: OrgCCItem, checked: boolean) => {
      onChange(
        checked
          ? value.some((selectedCc) => selectedCc === cc.id)
            ? value
            : [...value, cc.id]
          : value.filter((selectedCc) => selectedCc !== cc.id),
      );
    };

    return (
      <OrgAccordionContainer ref={ref}>
        <div className="chips-container">
          {org.areaList.filtered.map((area, index) => (
            <Chip
              key={`${index}-${area.id}`}
              option={{ value: area.id, label: area.name }}
              selectedChip={selectedArea.id === area.id}
              onSelectChip={() => setSelectedArea(area)}
              size={ChipSizeEnum.BIG}
            />
          ))}
        </div>
        <div className="toggle-container">
          {ALL_LABEL}
          <ToggleSwitch
            value={areAllSelectedInArea}
            onChange={toggleAllCcsInArea}
          />
        </div>
        <div className="accordion-list">
          {practiceList.map((practice, practiceIndex) => {
            const practiceCcList = practice.ccList;
            const selectedCcsForPractice = practiceCcList.filter((cc) =>
              value.some((selectedCc) => selectedCc === cc.id),
            );
            return (
              <Accordion
                key={`${practice.id}-${practiceIndex}`}
                label={practice.name}
                leftComponent={
                  <Checkbox
                    value={
                      selectedCcsForPractice.length === practice.ccList.length
                    }
                    onChange={(checked) =>
                      toggleAllCcsInPractice(practice, checked)
                    }
                    disabled={practiceCcList.length === 0}
                    {...(selectedCcsForPractice.length > 0 &&
                      selectedCcsForPractice.length <
                        practice.ccList.length && {
                        indeterminate: true,
                      })}
                  />
                }
                rightComponent={`${selectedCcsForPractice.length}/${practiceCcList.length}`}
                collapsible={practiceCcList.length > 0}
              >
                <OrgAccordionContent>
                  {practiceCcList.map((cc, ccIndex) => {
                    const isSelected = value.some(
                      (selectedCc) => selectedCc === cc.id,
                    );
                    return (
                      <OrgAccordionOption key={`${cc.id}-${ccIndex}`}>
                        <Checkbox
                          value={isSelected}
                          onChange={(checked) => toggleCc(cc, checked)}
                        />
                        {cc.name}
                      </OrgAccordionOption>
                    );
                  })}
                </OrgAccordionContent>
              </Accordion>
            );
          })}
        </div>
      </OrgAccordionContainer>
    );
  },
);
